import type { Store } from '@warp-drive/core';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import { executeSearchRequest } from 'tio-data/requests/search';
import type { DashboardContext } from 'tio-employee/utils/partner-dashboard/registry';

/** How many of the viewer's latest decisions the dashboard lists. */
export const RECENT_DECISIONS_SIZE = 5;

/** The decision-log outcomes that count as a reviewer decision; `other` events are excluded. */
export const DECISION_OUTCOMES = ['approve', 'reject', 'decline', 'request_change'] as const;

export type DecisionOutcome = (typeof DECISION_OUTCOMES)[number];

/** One decision the viewer made, reduced to what the dashboard lists and links to. */
export interface RecentDecision {
  id: string;
  employeeName: string;
  programName: string;
  outcome: DecisionOutcome;
  /** ISO timestamp of the decision. */
  decidedAt?: string;
  /** Review page for the decided record; absent when the log row carries neither id. */
  route?: string;
  model?: string;
}

interface DecisionDocument {
  id: string;
  tasEventTransitionEmployeeNameKeyword?: string;
  tasEventTransitionProgramTemplateNameKeyword?: string;
  tasEventTransitionDecisionKeyword?: string;
  tasEventTransitionEventTimestampDate?: string;
  tasEventTransitionApplicationIdKeyword?: string;
  tasEventTransitionProgramInstanceIdKeyword?: string;
}

function toDecision(doc: DecisionDocument): RecentDecision {
  const applicationId = doc.tasEventTransitionApplicationIdKeyword;
  const instanceId = doc.tasEventTransitionProgramInstanceIdKeyword;
  return {
    id: doc.id,
    employeeName: doc.tasEventTransitionEmployeeNameKeyword ?? '',
    programName: doc.tasEventTransitionProgramTemplateNameKeyword ?? '',
    outcome: (doc.tasEventTransitionDecisionKeyword ?? 'approve') as DecisionOutcome,
    decidedAt: doc.tasEventTransitionEventTimestampDate,
    ...(applicationId
      ? { route: 'authenticated.admin.tas.applications.approval', model: applicationId }
      : instanceId
        ? { route: 'authenticated.admin.tas.programs', model: instanceId }
        : {}),
  };
}

/**
 * The viewer's latest reviewer decisions at the current company, newest
 * first, from the TAS decision log (`TasEventTransition`). The API requires
 * the company filter for partner roles; the actor filter is by user id
 * because that is how the log records who decided.
 *
 * @throws (rejects) when the search request fails
 */
export async function loadRecentDecisions(ctx: DashboardContext): Promise<RecentDecision[]> {
  if (!ctx.userId || !ctx.companyId) return [];
  const store = ctx.owner.lookup('service:store') as Store;
  const { content } = await store.request<OpenSearchResponse>(
    executeSearchRequest({
      'model-type': 'TasEventTransition',
      'search-body': {
        size: RECENT_DECISIONS_SIZE,
        sort: [{ tas_event_transition_event_timestamp_date: { order: 'desc' } }],
        query: {
          bool: {
            filter: [
              { term: { company_id_keyword: ctx.companyId } },
              { term: { tas_event_transition_actor_user_id_keyword: ctx.userId } },
              { terms: { tas_event_transition_decision_keyword: [...DECISION_OUTCOMES] } },
            ],
          },
        },
      },
    })
  );
  const docs = OpenSearchResponseBuilder.from(
    content as OpenSearchResponse<DecisionDocument>
  ).getDocuments();
  return (docs as DecisionDocument[]).map(toDecision);
}

/** The median response time is taken over this many trailing days. */
export const RESPONSE_WINDOW_DAYS = 30;

/** How quickly the viewer has been deciding, for the approver hero tile. */
export interface DecisionStats {
  /** Decisions the viewer made in the last 7 days. */
  decidedThisWeek: number;
  /** Decisions in the last `RESPONSE_WINDOW_DAYS` days, which the median is taken over. */
  decidedInWindow: number;
  /** Median days from an item reaching the viewer to their decision; null with no decisions. */
  medianResponseDays: number | null;
}

/**
 * One size-0 decision-log search for the viewer's pace: decisions in the last
 * 7 days and the median response time over the last `RESPONSE_WINDOW_DAYS`.
 * Response time is the log's own measure, from entering the pending state to
 * the decision, so it counts nights and weekends.
 *
 * @throws (rejects) when the search request fails
 */
export async function loadDecisionStats(ctx: DashboardContext): Promise<DecisionStats> {
  if (!ctx.userId || !ctx.companyId) {
    return { decidedThisWeek: 0, decidedInWindow: 0, medianResponseDays: null };
  }
  const store = ctx.owner.lookup('service:store') as Store;
  const { content } = await store.request<OpenSearchResponse>(
    executeSearchRequest({
      'model-type': 'TasEventTransition',
      'search-body': {
        size: 0,
        query: {
          bool: {
            filter: [
              { term: { company_id_keyword: ctx.companyId } },
              { term: { tas_event_transition_actor_user_id_keyword: ctx.userId } },
              { terms: { tas_event_transition_decision_keyword: [...DECISION_OUTCOMES] } },
            ],
          },
        },
        aggs: {
          week: {
            filter: {
              range: { tas_event_transition_event_timestamp_date: { gte: 'now-7d', lte: 'now' } },
            },
          },
          window: {
            filter: {
              range: {
                tas_event_transition_event_timestamp_date: {
                  gte: `now-${RESPONSE_WINDOW_DAYS}d`,
                  lte: 'now',
                },
              },
            },
            aggs: {
              median: {
                percentiles: {
                  field: 'tas_event_transition_response_days_float',
                  percents: [50],
                  keyed: false,
                },
              },
            },
          },
        },
      },
    })
  );
  type Count = { doc_count?: number; docCount?: number };
  const aggregations = (content.aggregations ?? {}) as Record<string, unknown>;
  const week = aggregations['week'] as Count | undefined;
  const window = aggregations['window'] as
    (Count & { median?: { values?: { value?: number | null }[] } }) | undefined;
  const median = window?.median?.values?.[0]?.value;
  return {
    decidedThisWeek: week?.doc_count ?? week?.docCount ?? 0,
    decidedInWindow: window?.doc_count ?? window?.docCount ?? 0,
    medianResponseDays: typeof median === 'number' && Number.isFinite(median) ? median : null,
  };
}
